import React, { useState } from 'react';
import { AdminLayout } from '../../layout/AdminLayout';
import { Users, TrendingUp, Calendar } from 'lucide-react';
import { useAsync } from 'react-use';
import { useApiClient } from '../../base/hooks/useApiClient';
import { Table, Column } from '../../base/Table';

interface User {
    id: number;
    username: string;
    email: string;
    createdAt: string;
    lastLoginAt: string | null;
    balance: number;
}

interface UserStats {
    totalUsers: number;
    newUsersWeek: number;
    newUsersMonth: number;
}

interface UsersResponse {
    users: User[];
    pagination: {
        page: number;
        limit: number;
        totalCount: number;
        totalPages: number;
    };
}

export function AdminUsersPage() {
    const apiClient = useApiClient();
    const [page, setPage] = useState(1);
    const [sortColumn, setSortColumn] = useState('username');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const limit = 20;

    const { value: stats, loading: statsLoading } = useAsync(async () => {
        const response = await apiClient.get('admin/users/stats');
        if (!response.ok) {
            console.error('Error fetching user stats');
            return null;
        }
        return await response.json() as UserStats;
    });

    const { value: usersData, loading: usersLoading } = useAsync(async () => {
        const params = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
            sortBy: sortColumn,
            sortOrder: sortDirection,
        });
        
        const response = await apiClient.get(`admin/users?${params}`);
        if (!response.ok) {
            console.error('Error fetching users');
            return null;
        }
        return await response.json() as UsersResponse;
    }, [page, sortColumn, sortDirection]);

    const handleSortChange = (column: string, direction: 'asc' | 'desc') => {
        setSortColumn(column);
        setSortDirection(direction);
        setPage(1); // Reset to first page when sorting changes
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return 'Never';
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(new Date(dateString));
    };

    const columns: Column<User>[] = [
        {
            key: 'username',
            label: 'Username',
            render: (user) => (
                <div className="text-sm font-medium text-gray-900">
                    {user.username}
                </div>
            ),
        },
        {
            key: 'email',
            label: 'Email',
            render: (user) => (
                <div className="text-sm text-gray-600">{user.email}</div>
            ),
        },
        {
            key: 'createdAt',
            label: 'Registered At',
            columnType: 'datetime',
            render: (user) => (
                <div className="text-sm text-gray-600">
                    {formatDate(user.createdAt)}
                </div>
            ),
        },
        {
            key: 'lastLoginAt',
            label: 'Last Login',
            columnType: 'datetime',
            render: (user) => (
                <div className="text-sm text-gray-600">
                    {formatDate(user.lastLoginAt)}
                </div>
            ),
        },
        {
            key: 'balance',
            label: 'Balance',
            columnType: 'number',
            render: (user) => (
                <div className="text-sm text-gray-600">
                    ${Number(user.balance).toFixed(2)}
                </div>
            ),
        },
    ];

    return (
        <AdminLayout>
            <div className="max-w-7xl">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Users Management</h1>
                    <p className="mt-2 text-gray-600">Manage user accounts and monitor activity</p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Total Users</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">
                                    {statsLoading ? '-' : stats?.totalUsers || 0}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                                <Users className="h-6 w-6 text-indigo-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">New Users (Last Week)</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">
                                    {statsLoading ? '-' : stats?.newUsersWeek || 0}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                                <TrendingUp className="h-6 w-6 text-green-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">New Users (Last Month)</p>
                                <p className="text-2xl font-bold text-gray-900 mt-1">
                                    {statsLoading ? '-' : stats?.newUsersMonth || 0}
                                </p>
                            </div>
                            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                                <Calendar className="h-6 w-6 text-purple-600" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Users Table */}
                <Table
                    title="All Users"
                    data={usersData?.users}
                    columns={columns}
                    loading={usersLoading}
                    emptyMessage="No users found"
                    defaultSortKey="username"
                    defaultSortDirection="asc"
                    getRowKey={(user) => user.id}
                    pagination={{
                        itemsPerPage: limit,
                        currentPage: page,
                        totalItems: usersData?.pagination.totalCount || 0,
                        onPageChange: handlePageChange,
                    }}
                    sort={{
                        sortColumn,
                        sortDirection,
                        onSortChange: handleSortChange,
                    }}
                />
            </div>
        </AdminLayout>
    );
}
